import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, notification, Table } from "antd";
import Icon, { EyeOutlined, PlusOutlined } from "@ant-design/icons";
import { FiUsers } from "react-icons/fi";
import { NewMember } from "../../pages/admin/user-management/NewMember";
import EditMemberModal from "../../pages/admin/user-management/EditMemberModal";
import PreviewMemberModal from "../../pages/admin/user-management/PreviewMemberModal";
import { DeleteComponent } from "../../pages/admin/workspace/components/DeleteComponent";
import Filters from "../../ui/Filters/Filters";
import SearchFilter from "../../ui/SearchFilter/SearchFilter";
import ActionsIcons from "../../ui/ActionsIcons/ActionsIcons";
import { httpService } from "../../base/services/httpService.service";
import { getOrderedRoles } from "../../pages/admin/helpers";
import { createQueryParams } from "../../helpers/queryParams";
import AuthService from "../../base/services/authentication.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import useRowSelection from "../../base/hooks/useRowSelection";
import https from "../../base/utils/http";
import { EntityTypeEnum, ModalTypesEnum, UserRolesEnum } from "../../utils/enums";
import { EditIcon } from "../../assets/icons/EditIcon";
import { DeleteIcon } from "../../assets/icons/DeleteIcon";
import { genderOptions } from "../../utils/options";

import styles from "../../pages/admin/user-management/UserManagement.module.scss";

const PAGE_SIZE = 10;

const MembersView = React.memo(
   ({ workspaces = [], columns, projects = [], adminUserManagementPage = false }) => {
      const { rowSelection, onRow, selectedRowKeys, setSelectedRowKeys } = useRowSelection();

      const [loading, setLoading] = useState(false);
      const [visible, setVisible] = useState(false);
      const [rolesList, setRolesList] = useState([]);
      const [members, setMembers] = useState([]);
      const [countries, setCountries] = useState([]);
      const [languages, setLanguages] = useState([]);
      const [deleteModalOpen, setDeleteModalOpen] = useState("");
      const [deleteItemId, setDeleteItemId] = useState(null);
      const [editModalVisible, setEditModalVisible] = useState(false);
      const [previewModalVisible, setPreviewModalVisible] = useState(false);
      const [editMemberData, setEditMemberData] = useState(null);
      const [pagination, setPagination] = useState({
         current: 1,
         pageSize: PAGE_SIZE,
         total: 0
      });
      const [filterValue, setFilterValue] = useState({
         searchInput: "",
         roles: [],
         languages: [],
         genderValues: [],
         locations: [],
         workspaces: [],
         projects: []
      });

      const handleOpenModal = () => {
         setVisible(true);
      };

      const handleCloseModal = (modal) => {
         if (modal === "newMemberModal") {
            setVisible(false);
            return;
         }
         if (modal === "editMemberModal") {
            setEditModalVisible(false);
            return;
         }
         if (modal === "previewMemberModal") {
            setPreviewModalVisible(false);
            return;
         }
      };

      const getRoleIdAndName = (roleName) => {
         const simplifiedRoleMap = {
            SeniorProjectManager: "SeniorManager",
            JuniorProjectManager: "JuniorManager"
         };
         const simplifiedRoleName = simplifiedRoleMap[roleName] || roleName;
         const roleId = UserRolesEnum[simplifiedRoleName] || null;

         return { roleId, roleName: simplifiedRoleName };
      };

      const getAllProjectMembers = useCallback(
         async (page, pageSize) => {
            setLoading(true);
            const queryParams = createQueryParams(filterValue);
            const projectID = LocalStorageService.getItem("currentProjectID");

            const params = new URLSearchParams({
               isDeleted: false,
               Page: page,
               PageSize: pageSize,
               ProjectId: projectID
            });
            const additionalParams = new URLSearchParams(queryParams);
            for (const [key, value] of additionalParams) {
               params.append(key, value);
            }

            httpService.get(
               `/Projects/getAllProjectMembers/${projectID}?${params.toString()}`,
               (response) => {
                  const data = {
                     ...response.data,
                     members: response.data.members.map((member) => {
                        const { roleId, roleName } = getRoleIdAndName(member.role);
                        return {
                           key: member?.projectMemberId,
                           id: member.memberId,
                           fullName: member?.name,
                           gender: member?.gender,
                           email: member?.email,
                           organizationMemberId: member?.organizationMemberId,
                           projectMemberId: member?.projectMemberId,
                           selectedLanguages: member?.selectedLanguages.map((language) => ({
                              id: language.id,
                              name: language.name
                           })),
                           location: member?.location,
                           roles: [{ id: roleId, name: roleName }]
                        };
                     })
                  };
                  setMembers(data?.members);
                  setLoading(false);
                  setPagination({
                     ...pagination,
                     total: response.data.totalSize,
                     current: page,
                     pageSize: pageSize
                  });
               },
               (_err) => {
                  setLoading(false);
               }
            );
         },
         // eslint-disable-next-line react-hooks/exhaustive-deps
         [filterValue]
      );

      const getAllMembersOfOrganization = useCallback(
         async (page, pageSize) => {
            setLoading(true);
            const queryParams = createQueryParams(filterValue);
            await httpService.get(
               `/Members/getAllMembersOfOrganization?${queryParams}&page=${page}&pageSize=${pageSize}`,
               (res) => {
                  const allMembers = res.data?.members?.map((u) => ({
                     ...u,
                     key: u.organizationMemberId
                  }));
                  setMembers(allMembers);
                  setLoading(false);
                  setPagination((prevState) => ({
                     ...prevState,
                     current: page,
                     pageSize: pageSize,
                     total: res.data.totalSize
                  }));
               },
               (_error) => {
                  setLoading(false);
               }
            );
         },
         [filterValue]
      );

      const getAllOrganizationRoles = useCallback(() => {
         httpService.get("/Roles/getAllOrganizationRoles", (res) => {
            if (!res.data) return;
            const roles = getOrderedRoles(
               res.data?.filter(
                  (item) => item.name !== "Organization" && item.name !== "OrgSuperAdmin"
               )
            );

            setRolesList(
               roles.map((role) => ({
                  id: role.id,
                  name: role.name,
                  value: role.id,
                  label: role.name
               }))
            );
         });
      }, []);

      const getAllCountries = useCallback(() => {
         httpService.get(`/Languages/getAllCountries`, (res) => {
            if (!res.data) return;
            setCountries(
               res.data.map((country) => ({
                  value: country.id,
                  label: country.name
               }))
            );
         });
      }, []);

      const getAllLanguages = useCallback(() => {
         httpService.get(`Languages/getAll`, (res) => {
            if (!res.data) return;
            setLanguages(
               res.data.map((lang) => ({
                  value: lang.id,
                  label: lang.name
               }))
            );
         });
      }, []);

      useEffect(() => {
         if (filterValue.searchInput.trim() === "") {
            if (adminUserManagementPage) {
               getAllMembersOfOrganization(pagination.current, pagination.pageSize);
            } else {
               getAllProjectMembers(pagination.current, pagination.pageSize);
            }
            return;
         }

         const timeoutId = setTimeout(() => {
            if (adminUserManagementPage) {
               getAllMembersOfOrganization(pagination.current, pagination.pageSize);
            } else {
               getAllProjectMembers(pagination.current, pagination.pageSize);
            }
         }, 500);

         return () => clearTimeout(timeoutId);
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [filterValue, adminUserManagementPage]);

      useEffect(() => {
         getAllOrganizationRoles();
         getAllCountries();
         getAllLanguages();
         // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      const handlePagination = useCallback(
         (page, pageSize) => {
            adminUserManagementPage
               ? getAllMembersOfOrganization(page, pageSize)
               : getAllProjectMembers(page, pageSize);
            setPagination((prevS) => ({ ...prevS, current: page, pageSize }));
         },
         // eslint-disable-next-line react-hooks/exhaustive-deps
         [pagination]
      );

      const handleSearch = useCallback(
         (e) => {
            setPagination((prevS) => ({ ...prevS, current: 1 }));
            setFilterValue((prevS) => ({ ...prevS, searchInput: e.target.value }));
         },
         [setPagination, setFilterValue]
      );

      const handleClearFilters = useCallback(() => {
         setFilterValue((prevFilterValue) => ({
            ...prevFilterValue,
            searchInput: "",
            roles: [],
            languages: [],
            locations: [],
            genderValues: [],
            workspaces: [],
            projects: []
         }));
         setPagination((prevS) => ({ ...prevS, current: 1 }));
      }, []);

      const onSelect = useCallback((value, key) => {
         setFilterValue((prevS) => ({ ...prevS, [key]: value }));
         setPagination((prevS) => ({ ...prevS, current: 1 }));
      }, []);

      const openDeleteModal = (record) => {
         setDeleteItemId(record);
         setDeleteModalOpen(record.key);
      };

      const closeDeleteModal = () => {
         setDeleteModalOpen("");
         setDeleteItemId(null);
      };

      const additionalColumn = {
         title: "",
         key: "action",
         render: (_, record) => (
            <ActionsIcons
               actions={[
                  {
                     icon: <EyeOutlined />,
                     tooltip: adminUserManagementPage ? "View" : "View",
                     onClick: () => handlePreview(record)
                  },
                  {
                     icon: <EditIcon />,
                     tooltip: adminUserManagementPage ? "Edit" : "Edit",
                     onClick: () => handleEdit(record)
                  },
                  {
                     icon: <DeleteIcon color="#252938" />,
                     tooltip: adminUserManagementPage
                        ? "Delete"
                        : "Deleting a member here will soft delete them from this project only. To delete them from the organization, go to the User Management page.",
                     onClick: () => openDeleteModal(record)
                  }
               ]}
            />
         ),
         fixed: "right",
         width: 100
      };

      const membersColumns = AuthService.isAuthorized(
         adminUserManagementPage
            ? [UserRolesEnum.Organization, UserRolesEnum.SeniorProjectManager]
            : [
                 UserRolesEnum.SuperAdmin,
                 UserRolesEnum.Organization,
                 UserRolesEnum.AdminOfOrganization,
                 UserRolesEnum.SeniorProjectManager,
                 UserRolesEnum.ProjectManager
              ]
      )
         ? columns.concat(additionalColumn)
         : columns;

      const handleDelete = async (record) => {
         try {
            const params = new URLSearchParams({
               entityType: adminUserManagementPage
                  ? EntityTypeEnum.OrganizationMember
                  : EntityTypeEnum.ProjectMember,
               isDeleted: true
            });
            const res = await https.put(
               `/DataCollection/${
                  adminUserManagementPage ? record.organizationMemberId : record.projectMemberId
               }/toggleDeleteOrRestore?${params.toString()}`
            );

            notification.open({
               type: res.data.isSuccess ? "success" : "error",
               message: res.data.message || "Something went wrong! Please try again later."
            });
            if (res.data.isSuccess) {
               if (adminUserManagementPage) {
                  getAllMembersOfOrganization(1, 10);
               } else {
                  getAllProjectMembers(1, 10);
               }
            }
         } catch (_err) {
            notification.error({
               message: "Error deleting user, please try again later."
            });
         }
      };

      const handleBulkDelete = async () => {
         try {
            const params = new URLSearchParams({
               entityType: adminUserManagementPage
                  ? EntityTypeEnum.OrganizationMember
                  : EntityTypeEnum.ProjectMember,
               isDeleted: true
            });
            const res = await https.put(
               `/DataCollection/bulkToggleDeleteOrRestore?${params.toString()}`,
               selectedRowKeys
            );

            notification.open({
               type: res.data.isSuccess ? "success" : "error",
               message: res.data.message || "Something went wrong! Please try again later."
            });
            if (res.data.isSuccess) {
               setSelectedRowKeys([]);

               if (adminUserManagementPage) {
                  getAllMembersOfOrganization(1, 10);
               } else {
                  getAllProjectMembers(1, 10);
               }
            }
         } catch (err) {
            notification.error({
               message:
                  err.response?.data?.message || "Error deleting members, please try again later."
            });
         }
      };

      const handleEdit = (data) => {
         setEditMemberData(data); // Set data of the member being edited
         setEditModalVisible(true); // Open the Edit modal
      };
      const handlePreview = (data) => {
         setEditMemberData(data); // Set data of the member being edited
         setPreviewModalVisible(true); // Open the Edit modal
      };

      const filterConfigs = useMemo(
         () => [
            {
               key: "roles",
               label: "Role",
               placeholder: "Filter by role",
               options: rolesList,
               multiple: true
            },
            {
               key: "languages",
               label: "Language",
               placeholder: "Filter by language",
               options: languages,
               multiple: true
            },
            {
               key: "genderValues",
               label: "Gender",
               placeholder: "Filter by gender",
               options: genderOptions,
               multiple: true
            },
            {
               key: "locations",
               label: "Location",
               placeholder: "Filter by location",
               options: countries,
               multiple: true
            },
            ...(adminUserManagementPage
               ? [
                    {
                       key: "workspaces",
                       label: "Workspace",
                       placeholder: "Filter by workspace",
                       options: workspaces,
                       multiple: true
                    }
                 ]
               : []),
            ...(adminUserManagementPage
               ? [
                    {
                       key: "projects",
                       label: "Project",
                       placeholder: "Filter by project",
                       options: projects,
                       multiple: true
                    }
                 ]
               : [])
         ],
         [rolesList, languages, countries, workspaces, projects, adminUserManagementPage]
      );

      const canAddMember = adminUserManagementPage
         ? AuthService.isAuthorized([UserRolesEnum.Organization])
         : AuthService.isAuthorized([
              UserRolesEnum.SuperAdmin,
              UserRolesEnum.Organization,
              UserRolesEnum.AdminOfOrganization,
              UserRolesEnum.SeniorProjectManager,
              UserRolesEnum.ProjectManager,
              UserRolesEnum.JuniorProjectManager
           ]);

      const canDeleteMembers =
         (AuthService.isAuthorized([
            UserRolesEnum.SuperAdmin,
            UserRolesEnum.Organization,
            UserRolesEnum.AdminOfOrganization,
            UserRolesEnum.SeniorProjectManager,
            UserRolesEnum.ProjectManager
         ]) ||
            adminUserManagementPage) &&
         selectedRowKeys.length > 0;

      return (
         <div
            className="table-container project-members"
            style={{
               padding: "35px 25px",
               borderRadius: "8px"
            }}
         >
            <div className={styles.firstRowWrapper}>
               <h3 className={styles.title}>
                  User management
                  {adminUserManagementPage ? null : (
                     <span>
                        <FiUsers />
                        {pagination.total}
                     </span>
                  )}
               </h3>
               {canDeleteMembers ? (
                  <Button
                     className="kapSurveyPrimaryButton"
                     onClick={() => setDeleteModalOpen(ModalTypesEnum.BULK)}
                     icon={
                        <Icon
                           component={() => (
                              <svg
                                 width="16"
                                 height="16"
                                 viewBox="0 0 16 16"
                                 fill="none"
                                 xmlns="http://www.w3.org/2000/svg"
                              >
                                 <path
                                    d="M0.884033 4.04796H14.5983M4.88403 4.04796V3.47653C4.88403 2.71877 5.18505 1.99204 5.72087 1.45622C6.25668 0.920404 6.98342 0.619385 7.74118 0.619385C8.49894 0.619385 9.22567 0.920404 9.76149 1.45622C10.2973 1.99204 10.5983 2.71877 10.5983 3.47653V4.04796M6.02689 7.4782V12.0514M9.45546 7.4782V12.0514M2.59832 4.04796H12.884V14.3337C12.884 14.6368 12.7636 14.9275 12.5493 15.1418C12.335 15.3561 12.0443 15.4765 11.7412 15.4765H3.74118C3.43807 15.4765 3.14738 15.3561 2.93305 15.1418C2.71873 14.9275 2.59832 14.6368 2.59832 14.3337V4.04796Z"
                                    stroke="#fff"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                 />
                              </svg>
                           )}
                        />
                     }
                     style={{
                        fontFamily: "Poppins",
                        fontSize: "14px"
                     }}
                  >
                     Delete selected rows
                  </Button>
               ) : (
                  canAddMember && (
                     <Button
                        className="primaryButton"
                        icon={<PlusOutlined />}
                        disabled={loading}
                        loading={loading}
                        onClick={handleOpenModal}
                     >
                        {loading ? <>Processing</> : "Add member"}
                     </Button>
                  )
               )}
               {visible && (
                  <NewMember
                     visible={visible}
                     onCancel={handleCloseModal}
                     rolesList={rolesList.filter((item) =>
                        adminUserManagementPage
                           ? ["Senior Manager", "Project Manager", "Guest"].includes(item.name)
                           : AuthService.isAuthorized([UserRolesEnum.SeniorProjectManager])
                           ? ["Data Collector", "Junior Manager", "Project Manager"].includes(
                                item.name
                             )
                           : AuthService.isAuthorized([UserRolesEnum.JuniorProjectManager])
                           ? ["Data Collector"].includes(item.name)
                           : ["Data Collector", "Junior Manager"].includes(item.name)
                     )}
                     fetchTableData={() =>
                        adminUserManagementPage
                           ? getAllMembersOfOrganization(1, 10)
                           : getAllProjectMembers(1, 10)
                     }
                     downloadRole={adminUserManagementPage ? "admin" : "member"}
                     languages={languages}
                     countries={countries}
                     workspaces={workspaces}
                     adminUserManagementPage={adminUserManagementPage}
                  />
               )}
            </div>
            <p
               style={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  color: "#555",
                  marginBottom: "24px"
               }}
            >
               Here’s the list of the members in your workspace
            </p>

            <SearchFilter handleSearch={handleSearch} handleClearFilters={handleClearFilters}>
               <Filters
                  onSelectChange={onSelect}
                  filterValue={filterValue}
                  filterConfigs={filterConfigs}
               />
            </SearchFilter>

            <Table
               prefixCls="table-section"
               bordered
               columns={membersColumns}
               dataSource={members}
               rowSelection={
                  AuthService.isAuthorized([
                     UserRolesEnum.SuperAdmin,
                     UserRolesEnum.Organization,
                     UserRolesEnum.AdminOfOrganization,
                     UserRolesEnum.SeniorProjectManager,
                     UserRolesEnum.ProjectManager
                  ])
                     ? rowSelection
                     : undefined
               }
               onRow={onRow}
               scroll={{ x: "max-content" }}
               loading={loading}
               pagination={{
                  ...pagination,
                  showSizeChanger: true,
                  onChange: (page, pageSize) => {
                     handlePagination(page, pageSize);
                  }
               }}
            />
            {deleteModalOpen !== "" && deleteModalOpen !== ModalTypesEnum.BULK && (
               <DeleteComponent
                  open={deleteModalOpen.length > 0}
                  setOpen={() => setDeleteModalOpen("")}
                  showModal={deleteModalOpen.length > 0}
                  handleDelete={(record) => {
                     handleDelete(record);
                     closeDeleteModal();
                  }}
                  id={deleteItemId}
               />
            )}

            {deleteModalOpen === ModalTypesEnum.BULK && selectedRowKeys.length > 0 && (
               <DeleteComponent
                  open={deleteModalOpen === ModalTypesEnum.BULK && selectedRowKeys.length > 0}
                  setOpen={() => setDeleteModalOpen("")}
                  handleDelete={handleBulkDelete}
               />
            )}

            {editModalVisible && (
               <EditMemberModal
                  visible={editModalVisible}
                  onCancel={handleCloseModal}
                  editMemberData={editMemberData}
                  rolesList={rolesList}
                  languages={languages}
                  countries={countries}
                  workspaces={workspaces}
                  adminUserManagementPage={adminUserManagementPage}
                  fetchTableData={() =>
                     adminUserManagementPage
                        ? getAllMembersOfOrganization(pagination.current, pagination.pageSize)
                        : getAllProjectMembers(pagination.current, pagination.pageSize)
                  }
               />
            )}
            {previewModalVisible && (
               <PreviewMemberModal
                  visible={previewModalVisible}
                  onCancel={handleCloseModal}
                  editMemberData={editMemberData}
                  adminUserManagementPage={adminUserManagementPage}
               />
            )}
         </div>
      );
   }
);

export default MembersView;
